import { useCallback, useRef, useState } from 'react'

import type { SnackbarPayload } from 'components/Snackbar/useSnackbar'
import { ATTACHMENT_EXTENSIONS, ATTACHMENT_MAX_SIZE, ErrorMessages } from 'constants/Attachment'
import { upsertDeliveryWork } from 'repositories/ShipmentApi/upsertDeliveryWork'
import { downloadDeliveryWorkAttachment } from 'repositories/ShipmentApi/downloadDeliveryWorkAttachment'
import type {
  DeliveryWork,
  DeliveryWorkAttachment,
} from 'repositories/ShipmentApi/getShipment/type'
import { TruckbookApiResponseError } from 'repositories/TruckbookApiResponseError'
import { extractExtensionOfFileName } from 'utils/commonFunctions'

type ExistingAttachment = {
  id: number
  name: string
}

type Props = {
  shipmentUid: string
  // DB 永続済み visit の数値 ID。ローカル追加（未永続）の場合は null
  dbVisitId: number | null
  deliveryReportUid: string
  driverPhone: string
  initialDeliveryWork: DeliveryWork | null
  initialAttachments: DeliveryWorkAttachment[]
  notify: (payload: SnackbarPayload) => void
  // 保存成功後に呼ぶ。controller 側で getShipment 再取得し当該 visit を最新化する
  onSaved: (savedVisitId: number) => Promise<void>
}

// 既存の添付実装と同じ許可拡張子（png/jpg/jpeg/pdf/xls/xlsx）を再利用する
export const ACCEPT_ATTR = ATTACHMENT_EXTENSIONS.join(',')

// 空・空白・改行のみのメモは null（note part を送らない）に正規化する（§5.4 / §3.2）
export const normalizeNote = (memo: string): string | null => (memo.trim() === '' ? null : memo)

export const useDeliveryWorkForm = ({
  shipmentUid,
  dbVisitId,
  deliveryReportUid,
  driverPhone,
  initialDeliveryWork,
  initialAttachments,
  notify,
  onSaved,
}: Props) => {
  const [hasCargoWork, setHasCargoWork] = useState(initialDeliveryWork?.hasHandlingWork ?? false)
  const [memo, setMemo] = useState(initialDeliveryWork?.note ?? '')
  // 既存（DB 保存済み）添付。保存時に keptAttachmentIds として送る
  const [existingAttachments, setExistingAttachments] = useState<ExistingAttachment[]>(() =>
    initialAttachments.map((a) => ({ id: a.id, name: a.name }))
  )
  // 新規追加（未保存）の File 実体
  const [newFiles, setNewFiles] = useState<File[]>([])
  const [isSaving, setIsSaving] = useState(false)
  // 多重送信ガード用。isSaving を handleSave の deps に含めず参照を安定させるため ref で持つ
  const isSavingRef = useRef(false)
  // ダウンロード連打による多重実行を防ぐガード
  const isDownloadingRef = useRef(false)

  const isPersisted = dbVisitId !== null

  // 未保存（ドラフト）判定。現在の props（保存済みの最新値）を基準に差分を見る。
  // 保存成功後は props が最新値に更新され、ローカル state も一致するため dirty は解消する。
  const isDirty =
    hasCargoWork !== (initialDeliveryWork?.hasHandlingWork ?? false) ||
    memo !== (initialDeliveryWork?.note ?? '') ||
    newFiles.length > 0 ||
    existingAttachments.length !== initialAttachments.length

  const handleAddFile = useCallback(
    (file: File) => {
      // File.type(MIME) は OS/ブラウザ依存で空や octet-stream になり得るため拡張子で判定する
      const extension = extractExtensionOfFileName(file.name)
      if (!ATTACHMENT_EXTENSIONS.includes(`.${extension}`)) {
        notify({
          message: ErrorMessages.INVALID_FILE_TYPE.replace(/\$\{fileName\}/, file.name),
          variant: 'error',
        })
        return
      }
      // ちょうど 10MB は許可（> で判定）。超過のみ拒否
      if (file.size > ATTACHMENT_MAX_SIZE) {
        notify({
          message: ErrorMessages.OVER_MAX_SIZE.replace(/\$\{fileName\}/, file.name),
          variant: 'error',
        })
        return
      }
      // 既存添付名・追加済み新規ファイル名のいずれかと一致したら重複として拒否する
      const isDuplicate =
        existingAttachments.some((a) => a.name === file.name) ||
        newFiles.some((f) => f.name === file.name)
      if (isDuplicate) {
        notify({ message: ErrorMessages.DUPLICATED_FILE_NAME, variant: 'error' })
        return
      }
      setNewFiles((prev) => [...prev, file])
    },
    [notify, existingAttachments, newFiles]
  )

  const handleRemoveExistingAttachment = useCallback((id: number) => {
    setExistingAttachments((prev) => prev.filter((a) => a.id !== id))
  }, [])

  // 既存添付を直接ダウンロードする（プレビュー非対応の xls / xlsx 用）。
  // BE は Content-Disposition: attachment で返すため Blob を取得して a[download] で保存する
  const handleDownloadAttachment = useCallback(
    async (attachmentId: number, name: string) => {
      if (dbVisitId === null || isDownloadingRef.current) return
      isDownloadingRef.current = true
      try {
        const blob = await downloadDeliveryWorkAttachment({
          shipmentUid,
          visitId: dbVisitId,
          attachmentId,
          deliveryReportUid,
        })
        const url = URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = name
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
        // 即時 revoke するとダウンロードが始まらないブラウザがあるため遅延させる
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      } catch {
        notify({ message: 'ファイルの取得に失敗しました。', variant: 'error' })
      } finally {
        isDownloadingRef.current = false
      }
    },
    [shipmentUid, dbVisitId, deliveryReportUid, notify]
  )

  const handleRemoveNewFile = useCallback((index: number) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleSave = useCallback(async () => {
    if (!isPersisted || isSavingRef.current) return
    isSavingRef.current = true
    setIsSaving(true)
    try {
      const res = await upsertDeliveryWork({
        shipmentUid,
        visitId: dbVisitId,
        deliveryReportUid,
        driverPhone,
        hasHandlingWork: hasCargoWork,
        // 空・空白・改行のみは null 送信（BE で note=null 扱い）。非空はそのまま送る
        note: normalizeNote(memo),
        keptAttachmentIds: existingAttachments.map((a) => a.id),
        newAttachments: newFiles,
      })
      // 保存結果で状態を同期。新規ファイルは保存済みになったのでクリアする
      const { deliveryWork, deliveryWorkAttachments } = res.data
      setHasCargoWork(deliveryWork.hasHandlingWork)
      setMemo(deliveryWork.note ?? '')
      // 添付が無いとき BE が配列を省略するケースに備えてフォールバックする
      setExistingAttachments(
        (deliveryWorkAttachments ?? []).map((a) => ({ id: a.id, name: a.name }))
      )
      setNewFiles([])
      notify({ message: '保存しました。', variant: 'success' })
      // 保存後に shipment を再取得して当該 visit を最新化（描画の単一ソース化）。
      // 再取得失敗は保存成功を覆さない（表示はレスポンスで同期済み）
      try {
        await onSaved(dbVisitId)
      } catch {
        // noop
      }
    } catch (error) {
      const message =
        error instanceof TruckbookApiResponseError
          ? error.message || 'エラーが発生しました'
          : '通信エラーが発生しました'
      notify({ message, variant: 'error' })
    } finally {
      isSavingRef.current = false
      setIsSaving(false)
    }
  }, [
    isPersisted,
    shipmentUid,
    dbVisitId,
    deliveryReportUid,
    driverPhone,
    hasCargoWork,
    memo,
    existingAttachments,
    newFiles,
    notify,
    onSaved,
  ])

  return {
    hasCargoWork,
    setHasCargoWork,
    memo,
    setMemo,
    existingAttachments,
    newFiles,
    isSaving,
    isPersisted,
    isDirty,
    handleAddFile,
    handleRemoveExistingAttachment,
    handleRemoveNewFile,
    handleDownloadAttachment,
    handleSave,
  }
}
